/**
 * Chunked import — reads .xlsx or .csv in the browser, splits into fixed-size
 * CSV chunks, and uploads each chunk to /api/catalog/import-async as its own
 * background job. Combined progress is polled and reported via callbacks.
 *
 * This bypasses the Cloudflare 100 MB / 100 s edge limits because every upload
 * is a small (<5 MB) CSV that transfers in a few seconds.
 */

import api from "@/lib/api";

const CHUNK_SIZE = 25000; // rows per upload

/** Parse a .xlsx/.csv File into { header: string[], rows: any[][] } */
async function parseFileInBrowser(file) {
  const isXlsx = file.name.toLowerCase().endsWith(".xlsx") ||
                 file.name.toLowerCase().endsWith(".xls");
  const buf = await file.arrayBuffer();
  if (isXlsx) {
    const XLSX = await import("xlsx");
    const wb = XLSX.read(buf, { type: "array" });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
    if (!aoa.length) throw new Error("Excel file has no rows");
    const header = aoa[0].map((h) => (h == null ? "" : String(h)));
    // Filter out fully-empty data rows
    const rows = aoa.slice(1).filter((r) => r.some((v) => v !== "" && v != null));
    return { header, rows };
  }
  // CSV
  const text = new TextDecoder("utf-8").decode(buf);
  const parseLine = (line) => {
    const cells = [];
    let cur = ""; let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') {
        if (inQ && line[i + 1] === '"') { cur += '"'; i++; continue; }
        inQ = !inQ; continue;
      }
      if (c === "," && !inQ) { cells.push(cur); cur = ""; continue; }
      cur += c;
    }
    cells.push(cur);
    return cells;
  };
  const lines = text.split(/\r?\n/);
  // find first non-empty line as header
  let hIdx = 0;
  while (hIdx < lines.length && !lines[hIdx].trim()) hIdx++;
  if (hIdx >= lines.length) throw new Error("CSV is empty");
  const header = parseLine(lines[hIdx]);
  const rows = [];
  for (let i = hIdx + 1; i < lines.length; i++) {
    if (!lines[i].trim()) continue;
    rows.push(parseLine(lines[i]));
  }
  return { header, rows };
}

/** Convert a header + rows into a CSV Blob (properly quoted) */
function rowsToCsvBlob(header, rows) {
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    if (/[",\r\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
    return s;
  };
  const lines = [header.map(esc).join(",")];
  for (const r of rows) lines.push(r.map(esc).join(","));
  return new Blob([lines.join("\n")], { type: "text/csv" });
}

/**
 * Main entry point. Callback signatures:
 *   onSplit({ totalRows, chunkCount, chunkSize })
 *   onChunkQueued({ index, total, jobId })
 *   onProgress({ processed, total, added, skipped, errors, chunkStates })
 *   onDone({ added, skipped, errors, chunkStates })
 */
export async function chunkedImport(file, callbacks = {}) {
  const { onSplit, onChunkQueued, onProgress, onDone, onError } = callbacks;

  let header, rows;
  try {
    ({ header, rows } = await parseFileInBrowser(file));
  } catch (e) {
    onError?.(`Could not parse file: ${e.message}`);
    return null;
  }

  const totalRows = rows.length;
  const chunkCount = Math.max(1, Math.ceil(totalRows / CHUNK_SIZE));
  onSplit?.({ totalRows, chunkCount, chunkSize: CHUNK_SIZE });

  // Upload each chunk. We do this sequentially so we don't overwhelm
  // the server or Cloudflare with parallel large uploads.
  const chunkStates = [];
  for (let i = 0; i < chunkCount; i++) {
    const start = i * CHUNK_SIZE;
    const end = Math.min(totalRows, start + CHUNK_SIZE);
    const chunkRows = rows.slice(start, end);
    const blob = rowsToCsvBlob(header, chunkRows);
    const fd = new FormData();
    fd.append("file", blob, `${file.name.replace(/\.[^.]+$/, "")}-part${i + 1}.csv`);
    try {
      const { data } = await api.post("/catalog/import-async", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 2 * 60 * 1000,
      });
      chunkStates.push({
        index: i, total: chunkRows.length, jobId: data.job_id,
        status: "queued", processed: 0, added: 0, skipped: 0, errors: 0,
      });
      onChunkQueued?.({ index: i, total: chunkCount, jobId: data.job_id });
    } catch (e) {
      chunkStates.push({
        index: i, total: chunkRows.length, jobId: null,
        status: "upload_failed", error: e.message,
        processed: 0, added: 0, skipped: 0, errors: chunkRows.length,
      });
    }
  }

  // Poll all jobs until every one is terminal
  const isTerminal = (s) => ["completed", "failed", "interrupted", "upload_failed"].includes(s);
  while (chunkStates.some((c) => !isTerminal(c.status))) {
    await new Promise((r) => setTimeout(r, 2500));
    await Promise.all(chunkStates.map(async (c) => {
      if (isTerminal(c.status) || !c.jobId) return;
      try {
        const { data: j } = await api.get(`/catalog/import-jobs/${c.jobId}`);
        c.status = j.status;
        c.processed = j.processed || 0;
        c.added = j.added || 0;
        c.skipped = j.skipped || 0;
        c.errors = j.errors || 0;
        if (j.error_message || j.error) c.error = j.error_message || j.error;
        c.smart_categorized = j.smart_categorized || 0;
      } catch (_) { /* keep polling */ }
    }));
    const totals = chunkStates.reduce((a, c) => ({
      processed: a.processed + (c.processed || 0),
      total: a.total + (c.total || 0),
      added: a.added + (c.added || 0),
      skipped: a.skipped + (c.skipped || 0),
      errors: a.errors + (c.errors || 0),
      smart: a.smart + (c.smart_categorized || 0),
    }), { processed: 0, total: 0, added: 0, skipped: 0, errors: 0, smart: 0 });
    onProgress?.({ ...totals, chunkStates: [...chunkStates] });
  }

  const summary = chunkStates.reduce((a, c) => ({
    added: a.added + (c.added || 0),
    skipped: a.skipped + (c.skipped || 0),
    errors: a.errors + (c.errors || 0),
  }), { added: 0, skipped: 0, errors: 0 });
  const failedChunks = chunkStates.filter((c) => c.status === "upload_failed" || c.status === "failed");
  if (failedChunks.length > 0) {
    const detail = failedChunks[0].error || `${failedChunks.length} of ${chunkStates.length} chunks failed to upload`;
    onError?.(`${failedChunks.length}/${chunkStates.length} chunk(s) failed. ${detail}. ${summary.added} books imported before the failure.`);
    return { ...summary, chunkStates, failedChunks: failedChunks.length };
  }
  onDone?.({ ...summary, chunkStates });
  return summary;
}



/**
 * Client-side chunked BULK UPDATE. Same parsing + chunking as chunkedImport,
 * but hits POST /api/books/bulk-update per chunk and polls GET /books/bulk-update/{id}.
 *
 * Callbacks:
 *   onSplit({ totalRows, chunkCount, chunkSize })
 *   onChunkQueued({ index, total, jobId })
 *   onProgress({ processed, total, updated, not_found, smart_recategorized, chunkStates })
 *   onDone({ updated, not_found, smart_recategorized, notFoundSkus, chunkStates })
 *   onError(msg)
 */
export async function chunkedBulkUpdate(file, callbacks = {}) {
  const { onSplit, onChunkQueued, onProgress, onDone, onError } = callbacks;

  let header, rows;
  try {
    ({ header, rows } = await parseFileInBrowser(file));
  } catch (e) {
    onError?.(`Could not parse file: ${e.message}`);
    return null;
  }

  const totalRows = rows.length;
  const chunkCount = Math.max(1, Math.ceil(totalRows / CHUNK_SIZE));
  onSplit?.({ totalRows, chunkCount, chunkSize: CHUNK_SIZE });

  const chunkStates = [];
  for (let i = 0; i < chunkCount; i++) {
    const start = i * CHUNK_SIZE;
    const end = Math.min(totalRows, start + CHUNK_SIZE);
    const chunkRows = rows.slice(start, end);
    const blob = rowsToCsvBlob(header, chunkRows);
    const fd = new FormData();
    fd.append("file", blob, `${file.name.replace(/\.[^.]+$/, "")}-part${i + 1}.csv`);
    try {
      const { data } = await api.post("/books/bulk-update", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 2 * 60 * 1000,
      });
      chunkStates.push({
        index: i, total: chunkRows.length, jobId: data.job_id,
        status: "processing", processed: 0, updated: 0, not_found: 0,
        smart_recategorized: 0, not_found_skus: [],
      });
      onChunkQueued?.({ index: i, total: chunkCount, jobId: data.job_id });
    } catch (e) {
      chunkStates.push({
        index: i, total: chunkRows.length, jobId: null,
        status: "upload_failed", error: e.message,
        processed: 0, updated: 0, not_found: 0,
        smart_recategorized: 0, not_found_skus: [],
      });
    }
  }

  const isTerminal = (s) => ["done", "error", "upload_failed"].includes(s);
  while (chunkStates.some((c) => !isTerminal(c.status))) {
    await new Promise((r) => setTimeout(r, 2500));
    await Promise.all(chunkStates.map(async (c) => {
      if (isTerminal(c.status) || !c.jobId) return;
      try {
        const { data: j } = await api.get(`/books/bulk-update/${c.jobId}`);
        c.status = j.status;
        c.processed = j.processed || 0;
        c.updated = j.updated || 0;
        c.not_found = j.not_found || 0;
        c.smart_recategorized = j.smart_recategorized || 0;
        c.not_found_skus = j.not_found_skus || [];
        if (j.error) c.error = j.error;
      } catch (_) { /* keep polling */ }
    }));
    const totals = chunkStates.reduce((a, c) => ({
      processed: a.processed + (c.processed || 0),
      total: a.total + (c.total || 0),
      updated: a.updated + (c.updated || 0),
      not_found: a.not_found + (c.not_found || 0),
      smart_recategorized: a.smart_recategorized + (c.smart_recategorized || 0),
    }), { processed: 0, total: 0, updated: 0, not_found: 0, smart_recategorized: 0 });
    onProgress?.({ ...totals, chunkStates: [...chunkStates] });
  }

  const summary = chunkStates.reduce((a, c) => ({
    updated: a.updated + (c.updated || 0),
    not_found: a.not_found + (c.not_found || 0),
    smart_recategorized: a.smart_recategorized + (c.smart_recategorized || 0),
  }), { updated: 0, not_found: 0, smart_recategorized: 0 });
  const notFoundSkus = [...new Set(chunkStates.flatMap((c) => c.not_found_skus || []))].slice(0, 200);
  const failedChunks = chunkStates.filter((c) => c.status === "upload_failed" || c.status === "error");
  if (failedChunks.length > 0) {
    const detail = failedChunks[0].error || `${failedChunks.length} of ${chunkStates.length} chunks failed to upload`;
    onError?.(`${failedChunks.length}/${chunkStates.length} chunk(s) failed. ${detail}. ${summary.updated} rows were updated before the failure.`);
    return { ...summary, notFoundSkus, chunkStates, failedChunks: failedChunks.length };
  }
  onDone?.({ ...summary, notFoundSkus, chunkStates });
  return summary;
}
